import { Controller } from "@hotwired/stimulus"

// Botón "Formatear con IA" de los formularios de recursos y unidades.
// Cuelga del <form>; cada campo formateable es un target con
// data-formato-ia-tipo="html|texto". Regla dura: este controlador vive en el
// bundle principal y JAMÁS toca Quill directo (vive en el bundle editor):
// todo pasa por el hidden input y por CustomEvent ("ia:aplicar", "ia:paso").
//
// El servidor responde NDJSON en streaming: {"avance": 0.42} mientras la IA
// clasifica y al final {"contenido": …, "enlaces": […]} o {"error": …}. Los
// enlaces son videos llevados al formato del chip: se suman a Referencias.
const SIN_NOTICIAS_MS = 60_000 // aborta si el stream queda mudo este tiempo
const INVISIBLES = /[\s\u00A0\u00AD\u200B-\u200F\u2060\uFE0E\uFEFF]+/g

export default class extends Controller {
  static targets = ["campo"]
  // Mínimo de texto visible para ofrecer el botón (FormateadorIa::MINIMO_CARACTERES).
  static values = { minimo: { type: Number, default: 80 } }

  connect() {
    this.abortadores = new Set()
    // El editor avisa cada cambio con un "input" desde su hidden; el
    // textarea, con el suyo.
    this.alEscribir = (evento) => this.revisar(evento.target.closest?.("[data-formato-ia-tipo]"))
    this.element.addEventListener("input", this.alEscribir)
  }

  disconnect() {
    this.element.removeEventListener("input", this.alEscribir)
    this.abortadores.forEach((abortador) => abortador.abort())
    this.cerrarToast()
  }

  campoTargetConnected(campo) {
    this.revisar(campo)
  }

  // Con poco texto el botón se ve inactivo (aria-disabled y no `disabled`,
  // para que el título explique por qué y el clic lo diga).
  revisar(campo) {
    const boton = campo?.querySelector(".boton-ia")
    if (!boton) return

    const suficiente = this.suficiente(campo)
    boton.classList.toggle("is-insuficiente", !suficiente)
    boton.setAttribute("aria-disabled", String(!suficiente))
    boton.title = suficiente
      ? "Mejorar el formato sin cambiar el contenido"
      : `Escribe al menos ${this.minimoValue} caracteres para formatear con IA`
  }

  suficiente(campo) {
    const valor = this.contenidoDe(campo)
    const texto = campo.dataset.formatoIaTipo === "html"
      ? new DOMParser().parseFromString(valor, "text/html").body.textContent
      : valor
    return texto.replace(INVISIBLES, " ").trim().length >= this.minimoValue
  }

  formatearCampo(evento) {
    const campo = evento.currentTarget.closest("[data-formato-ia-tipo]")
    if (this.ocupado || !campo) return

    if (!this.suficiente(campo)) {
      return this.mostrarToast(`Escribe al menos ${this.minimoValue} caracteres para formatear con IA.`,
                               { error: true })
    }
    this.enLote(async () => {
      const ok = await this.formatear(campo)
      if (ok) this.mostrarToast("✨ Formateado", { deshacer: true })
    })
  }

  formatearTodo(evento) {
    evento.preventDefault()
    if (this.ocupado) return

    const campos = this.campoTargets.filter((campo) => this.suficiente(campo))
    if (campos.length === 0) {
      return this.mostrarToast("No hay contenido para formatear todavía.", { error: true })
    }
    // En paralelo: cada campo luce su propio avance y el botón global el
    // promedio. Un error en un campo no detiene a los demás.
    const global = evento.currentTarget
    this.enLote(async () => {
      this.avances = new Map(campos.map((campo) => [campo, 0]))
      this.botonGlobal = global
      this.marcarOcupado(global, true)
      const resultados = await Promise.all(campos.map((campo) => this.formatear(campo)))
      this.marcarOcupado(global, false)
      this.botonGlobal = null

      const listos = resultados.filter(Boolean).length
      if (listos > 0) {
        // Con resultados mixtos, el aviso cuenta también qué falló.
        const aviso = this.errores.length > 0 ? ` · ${this.errores[0]}` : ""
        this.mostrarToast(`✨ ${listos > 1 ? `${listos} campos formateados` : "Formateado"}${aviso}`,
                          { deshacer: true })
      }
    })
  }

  // Deshacer de la última operación completa (campo único o lote).
  deshacer() {
    this.cerrarToast()
    for (const { campo, valor } of this.previos || []) this.aplicar(campo, valor)
  }

  // ------- núcleo ----------------------------------------------------------

  async enLote(trabajo) {
    this.ocupado = true
    this.previos = []
    this.errores = []
    this.cerrarToast()
    this.botones().forEach((boton) => (boton.disabled = true))
    try {
      await trabajo()
    } finally {
      this.ocupado = false
      this.botones().forEach((boton) => (boton.disabled = false))
      this.campoTargets.forEach((campo) => this.revisar(campo))
      // En lote, los errores se juntan en un solo aviso al final.
      if (this.errores.length > 0 && !this.toast) {
        this.mostrarToast(this.errores[0], { error: true })
      }
    }
  }

  async formatear(campo) {
    const contenido = this.contenidoDe(campo)
    const boton = campo.querySelector(".boton-ia")
    const superficie = this.superficieDe(campo)
    superficie?.classList.add("ia-trabajando")
    this.marcarOcupado(boton, true)
    this.pulso(campo, "pulso")

    try {
      const resultado = await this.pedir(campo, contenido, (avance) => this.progreso(campo, avance))
      this.previos.push({ campo, valor: contenido })
      this.aplicar(campo, resultado.contenido)
      // Mismo canal que el chip de Video: la fila nueva en Referencias.
      for (const enlace of resultado.enlaces || []) {
        document.dispatchEvent(new CustomEvent("pauta:enlace", { detail: enlace }))
      }
      return true
    } catch (error) {
      const mensaje = error.name === "AbortError"
        ? "La IA dejó de responder y se canceló el formateo."
        : error.message || "No se pudo formatear. Inténtalo de nuevo."
      // En lote se antepone el nombre del paso para saber cuál falló.
      const nombre = this.botonGlobal && campo.closest("[data-titulo]")?.dataset.titulo
      this.errores.push(nombre ? `${nombre}: ${mensaje}` : mensaje)
      return false
    } finally {
      superficie?.classList.remove("ia-trabajando")
      this.marcarOcupado(boton, false)
      this.pulso(campo, "fin")
    }
  }

  // Lee el NDJSON a medida que llega. Devuelve { contenido, enlaces } o
  // lanza un Error con el mensaje del servidor.
  async pedir(campo, contenido, alAvanzar) {
    const abortador = new AbortController()
    this.abortadores.add(abortador)
    let vigilante = setTimeout(() => abortador.abort(), SIN_NOTICIAS_MS)
    const latido = () => {
      clearTimeout(vigilante)
      vigilante = setTimeout(() => abortador.abort(), SIN_NOTICIAS_MS)
    }

    try {
      const respuesta = await fetch("/ia/formatear", {
        method: "POST",
        signal: abortador.signal,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/x-ndjson, application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({
          contenido: contenido,
          tipo: campo.dataset.formatoIaTipo,
          titulo: this.element.querySelector("input[name$='[title]']")?.value,
          pauta: this.esPauta(campo)
        })
      })

      // Errores previos al stream (validación, sesión, key): JSON normal.
      if (!respuesta.ok) {
        const datos = await respuesta.json().catch(() => ({}))
        throw new Error(datos.error || "No se pudo formatear.")
      }

      const lector = respuesta.body.getReader()
      const decodificador = new TextDecoder()
      let pendiente = ""
      for (;;) {
        const { done, value } = await lector.read()
        if (done) break
        latido()
        pendiente += decodificador.decode(value, { stream: true })
        const lineas = pendiente.split("\n")
        pendiente = lineas.pop()
        for (const linea of lineas) {
          if (!linea.trim()) continue
          const datos = JSON.parse(linea)
          if (datos.error) throw new Error(datos.error)
          if ("contenido" in datos) return datos
          if ("avance" in datos) alAvanzar(datos.avance)
        }
      }
      throw new Error("La respuesta de la IA llegó incompleta. Inténtalo de nuevo.")
    } finally {
      clearTimeout(vigilante)
      this.abortadores.delete(abortador)
    }
  }

  // El avance se pinta en el botón del campo (vive en el encabezado pegajoso:
  // se ve aunque el editor sea largo) y, en lote, el promedio en el global.
  progreso(campo, avance) {
    this.pintarAvance(campo.querySelector(".boton-ia"), avance)
    if (this.botonGlobal && this.avances) {
      this.avances.set(campo, avance)
      const valores = [...this.avances.values()]
      this.pintarAvance(this.botonGlobal, valores.reduce((a, b) => a + b, 0) / valores.length)
    }
  }

  pintarAvance(boton, avance) {
    if (!boton) return
    const porcentaje = Math.round(Math.min(avance, 0.99) * 100)
    boton.style.setProperty("--ia-avance", `${porcentaje}%`)
    const texto = boton.querySelector(".ia-texto")
    if (texto) texto.textContent = `Formateando… ${porcentaje}%`
  }

  marcarOcupado(boton, ocupado) {
    if (!boton) return
    const texto = boton.querySelector(".ia-texto")
    if (ocupado) {
      if (texto && !texto.dataset.original) texto.dataset.original = texto.textContent
      this.pintarAvance(boton, 0)
    } else {
      if (texto?.dataset.original) texto.textContent = texto.dataset.original
      boton.style.removeProperty("--ia-avance")
    }
    boton.classList.toggle("is-ocupado", ocupado)
  }

  // Pauta de una clase: el campo muestra los chips de secciones. Solo ahí la
  // IA puede usar Inicio/Desarrollo/Cierre y el formato de video del chip.
  esPauta(campo) {
    const chip = campo.querySelector('button[data-accion="seccion"]')
    return Boolean(chip && !chip.classList.contains("hidden"))
  }

  contenidoDe(campo) {
    return this.entradaDe(campo)?.value || ""
  }

  entradaDe(campo) {
    return campo.dataset.formatoIaTipo === "html"
      ? campo.querySelector('[data-rich-text-target="input"]')
      : campo.querySelector("textarea")
  }

  // Lo que el usuario ve del campo: el editor (sin el label ni el encabezado)
  // o el textarea.
  superficieDe(campo) {
    return campo.querySelector(".quill-wrapper") || this.entradaDe(campo)
  }

  aplicar(campo, valor) {
    if (campo.dataset.formatoIaTipo === "html") {
      // El bundle editor lo escucha (ia:aplicar->rich-text#aplicarIa).
      campo.dispatchEvent(new CustomEvent("ia:aplicar", { detail: { html: valor } }))
    } else {
      const area = this.entradaDe(campo)
      area.value = valor
      area.dispatchEvent(new Event("input", { bubbles: true })) // índice de avance
    }
    const visible = this.superficieDe(campo)
    visible?.classList.remove("ia-revelado")
    void visible?.offsetWidth // reinicia la animación si se repite
    visible?.classList.add("ia-revelado")
    visible?.addEventListener("animationend", () => visible.classList.remove("ia-revelado"),
                              { once: true })
  }

  pulso(campo, estado) {
    campo.dispatchEvent(new CustomEvent("ia:paso", { detail: { estado }, bubbles: true }))
  }

  botones() {
    return [...this.element.querySelectorAll(".boton-ia")]
  }

  cerrarToast() {
    this.toast?.remove()
    this.toast = null
  }

  mostrarToast(mensaje, { deshacer = false, error = false } = {}) {
    this.cerrarToast()
    const toast = document.createElement("div")
    toast.className = `ia-toast${error ? " is-error" : ""}`
    toast.append(mensaje)
    if (deshacer) {
      const boton = document.createElement("button")
      boton.type = "button"
      boton.textContent = "Deshacer"
      boton.addEventListener("click", () => this.deshacer())
      toast.append(boton)
    }
    // Junto a los demás avisos del sitio (#flash).
    ;(document.getElementById("flash") || document.body).appendChild(toast)
    this.toast = toast
    setTimeout(() => {
      if (this.toast === toast) {
        toast.remove()
        this.toast = null
      }
    }, 8000)
  }
}
